import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPageBySlug, fetchCollection, getLocalizedValue } from '../lib/cms';
import usePageMetadata from '../hooks/usePageMetadata';
import TestimonialCard from '../components/TestimonialCard';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

export default function Experience() {
  const [pageData, setPageData] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    async function loadData() {
      try {
        const [page, expList, testimonialList] = await Promise.all([
          fetchPageBySlug('erfaring'),
          fetchCollection('experience'),
          fetchCollection('testimonials')
        ]);
        setPageData(page);
        // Sort experiences by period (newest first)
        setExperiences(expList.sort((a, b) => {
          const periodB = getLocalizedValue(b.period, language);
          const periodA = getLocalizedValue(a.period, language);
          return periodB.localeCompare(periodA);
        }));
        setTestimonials(testimonialList);
      } catch (err) {
        console.error('Error loading Experience page data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  // Set page metadata
  const title = pageData ? getLocalizedValue(pageData.meta_title, language) : 'Erfaring & Cases - Insightly ApS';
  const description = pageData ? getLocalizedValue(pageData.meta_description, language) : 'Se René Vilsgaards professionelle erfaring og cases, herunder engagements hos Lagkagehuset A/S og Micro Matic A/S.';
  usePageMetadata(title, description);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-offwhite">
        <div className="flex flex-col items-center gap-3">
          <i className="fa-solid fa-spinner animate-spin text-3xl text-petrol"></i>
          <p className="text-sm text-navy-900/60 font-medium">Henter indhold...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-offwhite py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial Header */}
        <div className="max-w-3xl mb-16 space-y-4">
          <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.home.trackRecordTag}</span>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium text-navy-900 leading-tight">
            {pageData ? getLocalizedValue(pageData.title, language) : t.experience.title}
          </h1>
          <p className="text-lg text-navy-900/60 font-sans">
            {t.experience.subtitle}
          </p>
        </div>

        {/* Intro Text from CMS */}
        <div className="max-w-4xl bg-white rounded-none border border-bluegray p-8 sm:p-10 shadow-xl mb-16">
          <div 
            className="prose prose-navy max-w-none text-navy-900/80 leading-relaxed font-sans text-base sm:text-lg"
            dangerouslySetInnerHTML={{ __html: pageData ? getLocalizedValue(pageData.content, language) : '' }}
          />
        </div>

        {/* Experiences Timeline/List */}
        <div className="space-y-12">
          {experiences.map((exp, idx) => (
            <div 
              key={exp.id} 
              className="bg-white rounded-none border border-bluegray p-8 sm:p-10 shadow-xl hover:shadow-2xl transition-shadow duration-300 grid grid-cols-1 lg:grid-cols-12 gap-8 relative"
            >
              <span className="text-bluegray/20 text-6xl font-serif font-bold absolute top-4 right-6 pointer-events-none select-none">0{idx + 1}</span>
              {/* Left Column - Meta */}
              <div className="lg:col-span-4 space-y-4">
                <span className="inline-block text-[10px] font-bold text-petrol uppercase tracking-widest bg-petrol-light px-3 py-1 rounded-none border border-petrol/20">
                  {getLocalizedValue(exp.period, language)}
                </span>
                <h3 className="font-serif text-2xl font-bold text-navy-900">{getLocalizedValue(exp.company, language)}</h3>
                <p className="text-xs text-navy-900/60 font-bold uppercase tracking-wider">{getLocalizedValue(exp.role, language)}</p>
                
                {/* Skills Badge List */}
                <div className="pt-4 border-t border-bluegray/60">
                  <p className="text-[10px] font-bold text-navy-900/50 uppercase tracking-widest mb-2">// {t.experience.skills}</p>
                  <div className="flex flex-wrap gap-2">
                    {getLocalizedValue(exp.skills, language).split(',').map((skill, idx) => (
                      <span key={idx} className="text-xs bg-navy-50 text-navy-900 font-medium px-2.5 py-1 rounded-none border border-bluegray/40">
                        {skill.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Detailed Description */}
              <div className="lg:col-span-8 space-y-4">
                <h4 className="font-serif text-lg font-bold text-navy-900">{t.home.projectDescTitle}</h4>
                <div 
                  className="prose prose-navy text-sm sm:text-base text-charcoal/80 leading-relaxed space-y-4 font-sans"
                  dangerouslySetInnerHTML={{ __html: getLocalizedValue(exp.description, language) }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Testimonials Section */}
        {testimonials.length > 0 && (
          <div className="mt-24 border-t border-bluegray/60 pt-20">
            <div className="max-w-3xl mb-12 space-y-3">
              <span className="text-xs uppercase tracking-[0.25em] text-petrol font-bold">// {t.home.customersTag}</span>
              <h2 className="text-3xl font-serif font-medium text-navy-900">
                {t.home.testimonialsTitle}
              </h2>
              <p className="text-base text-navy-900/60 font-sans">
                {t.home.testimonialsSubtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
              {testimonials.map((item, idx) => (
                <TestimonialCard key={item.id || idx} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-24 bg-navy-900 text-offwhite rounded-none p-8 sm:p-12 text-center max-w-4xl mx-auto space-y-6 shadow-2xl relative overflow-hidden">
          <div className="relative z-10 space-y-6">
            <h2 className="font-serif text-3xl font-medium">{t.home.ctaTitle}</h2>
            <p className="text-offwhite/80 max-w-2xl mx-auto text-sm sm:text-base font-sans leading-relaxed">
              {t.home.ctaText}
            </p>
            <div className="pt-4 flex flex-wrap justify-center gap-4">
              <Link
                to="/kontakt"
                className="bg-petrol hover:bg-petrol-dark text-offwhite px-8 py-4 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-md"
              >
                {t.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
